import React, { useState, useEffect } from 'react';
import { X, Coins, Calendar } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { format } from 'date-fns';
import { auth } from '@/services/firebase';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';

interface UsageLog {
  id: string;
  action: string;
  cost: number;
  promptTokens: number;
  completionTokens: number;
  timestamp: string | Date;
}

interface UsageHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  analyzeResume: 'Resume Analysis',
  generateCoverLetter: 'Cover Letter Generation',
  extractJobMetadata: 'Job Parsing',
  generateInterviewPrep: 'Interview Prep',
  generateCommunications: 'Outreach Email Generation',
  searchJobDescriptionWithAI: 'Job Description Import',
  cleanJobDescriptionWithAI: 'Job Description Cleanup',
  searchForJobs: 'Job Search',
  generateRefinedResume: 'Tailored Resume Generation',
};

function UsageItem({ log }: { log: UsageLog }) {
  const displayAction = ACTION_LABELS[log.action] || log.action;
  
  return (
    <div className="py-4 first:pt-0 last:pb-0 group">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div>
          <div className="font-semibold text-slate-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
            {displayAction}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-1">
            <Calendar className="w-3.5 h-3.5" />
            {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
          </div>
        </div>
        <div className="sm:text-right">
          <div className="font-medium text-slate-900 dark:text-white">
            {log.cost === 0 ? 'Free' : `-${log.cost.toFixed(2)} Credits`}
          </div>
          <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mt-1 inline-flex sm:flex uppercase tracking-wider bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 rounded">
            {log.promptTokens + log.completionTokens} Tokens
          </div>
        </div>
      </div>
    </div>
  );
}

function UsageSkeleton() {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div className="space-y-2.5 w-full sm:w-auto">
          <div className="h-5 w-40 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
          <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
        </div>
        <div className="sm:text-right space-y-2.5 w-full sm:w-auto flex flex-col sm:items-end">
          <div className="h-5 w-20 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
          <div className="h-4 w-16 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse mt-1"></div>
        </div>
      </div>
    </div>
  );
}

export function UsageHistoryModal({ isOpen, onClose }: UsageHistoryModalProps) {
  const [usages, setUsages] = useState<UsageLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUsages() {
      if (!isOpen) return;
      try {
        setLoading(true);
        const currentUser = auth.currentUser;
        
        let token;
        if (currentUser) {
          token = await currentUser.getIdToken();
        }

        const response = await fetch('/api/user/usages', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch usage history');
        }

        const data = await response.json();
        setUsages(data.usages || []);
      } catch (error: any) {
        toast.error(error.message || 'Could not load usage history');
      } finally {
        setLoading(false);
      }
    }

    fetchUsages();
  }, [isOpen]);

  return (
    <ResponsiveOverlay isOpen={isOpen} onClose={onClose} className="p-6 sm:max-w-md w-full max-h-[90vh]">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-amber-600 dark:text-amber-400 border border-slate-200 dark:border-slate-700">
            <Coins className="w-5 h-5" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Usage History</h2>
        </div>
        <Button 
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close history"
          className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="overflow-y-auto pr-2 pb-2 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 max-h-[60vh] space-y-0 divide-y divide-slate-100 dark:divide-slate-800/60">
        {loading ? (
          <>
            <UsageSkeleton />
            <UsageSkeleton />
            <UsageSkeleton />
          </>
        ) : usages.length > 0 ? (
          usages.map((log) => (
            <UsageItem key={log.id} log={log} />
          ))
        ) : (
          <div className="text-center py-12 px-4">
            <Coins className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-900 dark:text-white font-medium">No usage history found</p>
            <p className="text-sm text-slate-500 mt-1">When you use AI features, the credit usage will appear here.</p>
          </div>
        )}
      </div>
    </ResponsiveOverlay>
  );
}
